import { useEffect, useState } from "react";
import { Messages, Prospects, FollowUps, Replies } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Envelope, ChatCircleText, PaperPlaneRight, Clock, CheckCircle, XCircle, Lightning, Sparkle, ArrowBendUpLeft, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const statusIcon = {
  draft: Clock,
  queued: Clock,
  sent: CheckCircle,
  failed: XCircle,
  replied: CheckCircle,
};

const statusColor = {
  draft: "text-neutral-500",
  queued: "text-amber-600",
  sent: "text-emerald-600",
  failed: "text-red-600",
  replied: "text-blue-600",
};

export default function Inbox() {
  const [msgs, setMsgs] = useState([]);
  const [prospectMap, setProspectMap] = useState({});
  const [filter, setFilter] = useState("all");
  const [replyFor, setReplyFor] = useState(null);

  const load = async () => {
    const params = {};
    if (filter !== "all") params.status = filter;
    const list = await Messages.list(params);
    setMsgs(list);
    const ps = await Prospects.list();
    setProspectMap(Object.fromEntries(ps.map(p => [p.id, p])));
  };
  useEffect(() => { load(); }, [filter]);

  const sendNow = async (id) => {
    try {
      const res = await Messages.send(id);
      if (res.mailto_url) window.open(res.mailto_url, "_blank");
      toast.success("Sent");
      load();
    } catch (err) { toast.error("Send failed"); }
  };

  return (
    <div data-testid="inbox-page">
      <PageHeader
        overline="Outbound · History"
        title="Inbox"
        description="All drafted, queued, and sent messages across every campaign. Auto follow-ups fire every 15 min."
        testId="inbox-header"
        actions={
          <Button variant="outline" onClick={async () => {
            try {
              const r = await FollowUps.dispatchNow();
              toast.success(`Processed ${r.processed}: ${r.sent} sent, ${r.skipped} skipped, ${r.failed} failed`);
              load();
            } catch { toast.error("Dispatch failed"); }
          }} data-testid="dispatch-now-btn">
            <Lightning size={16} className="mr-2" /> Run follow-ups now
          </Button>
        }
      />
      <div className="p-10 space-y-4">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="inbox-tabs">
            <TabsTrigger value="all" data-testid="tab-inbox-all">All</TabsTrigger>
            <TabsTrigger value="draft" data-testid="tab-inbox-draft">Drafts</TabsTrigger>
            <TabsTrigger value="queued" data-testid="tab-inbox-queued">Queued</TabsTrigger>
            <TabsTrigger value="sent" data-testid="tab-inbox-sent">Sent</TabsTrigger>
            <TabsTrigger value="failed" data-testid="tab-inbox-failed">Failed</TabsTrigger>
          </TabsList>
        </Tabs>

        <Card className="rounded-md border-neutral-200 overflow-hidden">
          <div className="stagger-in">
            {msgs.length === 0 && <div className="py-16 text-center text-sm text-neutral-500">No messages yet.</div>}
            {msgs.map(m => {
              const p = prospectMap[m.prospect_id];
              const Icon = statusIcon[m.status] || Clock;
              return (
                <div key={m.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-neutral-100 row-hover items-center" data-testid={`msg-row-${m.id}`}>
                  <div className="col-span-1">
                    {m.channel === "email" ? <Envelope size={16} weight="duotone" /> : <ChatCircleText size={16} weight="duotone" />}
                  </div>
                  <div className="col-span-3">
                    <div className="text-sm font-medium">{p?.business_name || "—"}</div>
                    <div className="text-xs text-neutral-500 font-mono">{p?.email || p?.phone}</div>
                  </div>
                  <div className="col-span-5">
                    {m.subject && <div className="text-xs font-mono text-neutral-500 mb-1">Subject: {m.subject}</div>}
                    <div className="text-sm text-neutral-800 line-clamp-2">{m.body}</div>
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <Icon size={14} className={statusColor[m.status]} />
                    <span className={`text-xs font-mono uppercase tracking-widest ${statusColor[m.status]}`}>{m.status}</span>
                    {m.step > 0 && <Badge variant="outline" className="text-[9px] font-mono">FU {m.step}</Badge>}
                    {m.channel === "email" && m.open_count > 0 && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700"
                        title={m.first_opened_at ? `First opened ${new Date(m.first_opened_at).toLocaleString()}` : ""}
                        data-testid={`open-count-${m.id}`}
                      >
                        <Eye size={12} weight="fill" /> {m.open_count}
                      </span>
                    )}
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    {(m.status === "draft" || m.status === "queued") && (
                      <Button size="sm" variant="outline" onClick={() => sendNow(m.id)} data-testid={`send-${m.id}`}>
                        <PaperPlaneRight size={12} />
                      </Button>
                    )}
                    {m.status === "replied" && (
                      <Button size="sm" variant="outline" onClick={() => setReplyFor({ message: m, prospect: p })} data-testid={`respond-${m.id}`} title="Draft response">
                        <ArrowBendUpLeft size={12} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
      <ResponseDialog data={replyFor} onClose={() => setReplyFor(null)} onSent={load} />
    </div>
  );
}

function ResponseDialog({ data, onClose, onSent }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickedIdx, setPickedIdx] = useState(null);
  const [editable, setEditable] = useState("");

  useEffect(() => {
    if (data) {
      setOptions([]); setPickedIdx(null); setEditable("");
      setLoading(true);
      Replies.draft(data.prospect.id, data.message.id)
        .then(r => { setOptions(r.options || []); if (r.options?.length) { setPickedIdx(0); setEditable(r.options[0].body); } })
        .catch(() => toast.error("Failed to draft responses"))
        .finally(() => setLoading(false));
    }
  }, [data]);

  const pick = (i) => { setPickedIdx(i); setEditable(options[i].body); };

  const sendIt = async () => {
    if (!data?.prospect?.email) return toast.error("Prospect has no email");
    const mailto = `mailto:${data.prospect.email}?subject=${encodeURIComponent("Re: " + (data.message.subject || ""))}&body=${encodeURIComponent(editable)}`;
    window.open(mailto, "_blank");
    toast.success("Opening your mail client...");
    onSent();
    onClose();
  };

  if (!data) return null;
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="response-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Reply to {data.prospect?.business_name}</DialogTitle>
          <DialogDescription>Claude Sonnet 5 drafted 3 responses with different strategic angles.</DialogDescription>
        </DialogHeader>

        {loading && <div className="py-10 text-center text-sm text-neutral-500"><Sparkle size={16} className="inline mr-2" />Drafting...</div>}

        {!loading && options.length > 0 && (
          <>
            <div className="grid grid-cols-3 gap-3">
              {options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => pick(i)}
                  data-testid={`option-${i}`}
                  className={`text-left p-3 rounded-md border transition-colors ${pickedIdx === i ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 hover:border-neutral-500"}`}
                >
                  <div className={`overline mb-2 ${pickedIdx === i ? "text-neutral-400" : ""}`}>Option {i + 1}</div>
                  <div className={`text-xs font-mono uppercase tracking-widest ${pickedIdx === i ? "text-white" : "text-neutral-500"}`}>{opt.strategy}</div>
                  <div className={`text-xs mt-2 line-clamp-4 ${pickedIdx === i ? "text-neutral-200" : "text-neutral-700"}`}>{opt.body}</div>
                </button>
              ))}
            </div>

            <div>
              <div className="overline mb-2">Edit before sending</div>
              <Textarea rows={8} value={editable} onChange={e => setEditable(e.target.value)} data-testid="response-body" />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-neutral-900 hover:bg-black text-white" onClick={sendIt} data-testid="send-response-btn">
                <PaperPlaneRight size={14} className="mr-2" /> Send via my mail client
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
